import * as THREE from 'three';
import { CK, CLABEL, solveCalibration } from '../core/calibrate.js';
import { solveCornerPanel } from '../core/corner.js';
import { refineCornerPose } from '../core/corner-refine.js';
import { remapCorners } from '../core/crop-map.js';
import { frameOrbit } from '../scene/cameras.js';
import { rebuildHelpers } from '../scene/helpers.js';
import { addObject, importGLB, select, syncTransform } from '../scene/objects.js';
import { requestRender } from '../state/bus.js';
import { resetHistory } from '../state/history.js';
import { renderLeft } from '../ui/rail-left.js';
import { onResChange, onScreenChange } from '../ui/rail-right.js';
import { $, S, clamp, el, fmt } from '../state/store.js';
import { fitCalibStage, syncPlan } from './plan-view.js';
import { cropPhoto } from '../ui/crop-view.js';
import { pickFrame } from '../ui/frame-picker.js';
import { adoptSiteClip } from '../export/site-clip.js';
import { openClip } from '../unwrap/decode.js';
import { goStage, renderRails, setView } from '../ui/rails.js';
import { toast } from '../ui/toast.js';
import { track } from '../services/analytics.js';
import { MOTION_CTA, canUseScreenArray } from '../services/limits.js';
import { showTrial } from '../ui/trial.js';

/* ═══════════════════════════════════════════════════════════
   CALIBRATE  ·  photo, handles, loupe
   ═══════════════════════════════════════════════════════════ */
export const wrap = $('#calibWrap'), cimg = $('#calibImg'), poly = $('#calibPoly'), loupe = $('#loupe');

/* Lock the angle: freezes the four corners so the viewpoint they define can't be
   nudged by accident once you're happy with it. The label and icon track the
   state, so the one button both shows whether it's locked and flips it. */
const LOCK_ICONS = {
  open:'<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3.5" y="7.5" width="9" height="6.5" rx="1.4"/><path d="M5.2 7.5V5.2a2.8 2.8 0 0 1 5.4-1"/></svg>',
  shut:'<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3.5" y="7.5" width="9" height="6.5" rx="1.4"/><path d="M5.4 7.5V5.2a2.6 2.6 0 0 1 5.2 0V7.5"/></svg>'
};
const lockBtn = $('#lockAngle');
const goBtn = $('#goCompose');
const footHint = $('#calibFootHint');
goBtn.innerHTML = '<span>Go design</span>' +
  '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"/></svg>';
goBtn.addEventListener('click', ()=>{
  /* No solve on the placement path — that is the whole guarantee. Straight to
     the mockup, which is the only thing those marks are for. */
  if(placementOnly()){ goStage('compose'); setView('site'); return; }
  doSolve();                       // commit the locked corners to a viewpoint
  if(S.solved) goStage('compose'); // only advance if the solve actually succeeded
});
/* PLACEMENT ONLY — an LCD video wall whose viewer was set by hand.

   An array's SIZE is specified, not measured: it comes from the grid you typed.
   What a photograph can still measure is where the audience stands, which is
   the same thing it tells you about a single screen — so a wall set up WITH a
   photo is calibrated exactly like one (the four marks go on the outside
   corners of the whole wall, and the solve puts the viewer there).

   The other way in is to skip the photo. That path places a camera object, and
   a camera object IS the viewer (scene/helpers.js's eyeVec takes it over any
   solved eye), so a solve could not move it. There, a photo added later can only
   place the mockup: On-site composites by a homography built straight from the
   marks (export/onsite.js), never from the solved eye, so nothing has to be
   solved and nothing about the wall or the viewer changes.

   So it is derived from what is actually in the scene rather than kept as a
   mode: no second state to fall out of step with the first, and it survives a
   save and reload for free. */
export const placementOnly = () => !!S.screenArray && S.objects.some(o => o.type === 'camera');

/* Exported because the footer's two states depend on something OUTSIDE this
   file: whether the project has an LCD video wall. A photo loaded first and an
   array made second left the footer still offering Lock angle and Go design on
   a path where neither means anything — paintLock only ran when a photo
   arrived. onScreenChange is where every array change already funnels, so that
   is where this is called from. */
export function syncCalibFoot(){ paintLock(); }

function paintLock(){
  /* Placement has nothing to lock: locking exists to stop a viewpoint drifting
     once it has been solved for, and nothing is being solved. One button, and
     it goes where the mockup is. */
  if(placementOnly()){
    lockBtn.classList.add('hidden');
    goBtn.classList.remove('hidden');
    goBtn.innerHTML = '<span>Use for On-site</span>' +
      '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"/></svg>';
    footHint.textContent = 'Drag the four crosshairs onto the corners of the whole array. '
      + 'This places the mockup only — it changes nothing about the wall or the files it exports.';
    return;
  }
  lockBtn.classList.remove('hidden');
  goBtn.innerHTML = '<span>Go design</span>' +
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"/></svg>';
  const on = !!S.cornersLocked;
  lockBtn.classList.toggle('on', on);
  lockBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
  lockBtn.innerHTML = (on ? LOCK_ICONS.shut : LOCK_ICONS.open) +
    '<span>' + (on ? 'Angle locked' : 'Lock angle') + '</span>';
  lockBtn.title = on
    ? 'Corners are frozen. Click to unlock and adjust them.'
    : 'Freeze the four corners so the viewpoint can\u2019t shift by accident.';
  wrap.classList.toggle('locked', on);
  // the shortcut into compose only makes sense once the corners are committed
  goBtn.classList.toggle('hidden', !on);
  footHint.textContent = on
    ? 'Angle locked \u2014 ready for Design.'
    : 'Drag each crosshair onto a corner, then lock the angle.';
}
export function setCornersLocked(v){
  S.cornersLocked = !!v;
  paintLock();
  if(S.cornersLocked) loupe.style.display='none';
  toast(S.cornersLocked
    ? 'Angle locked \u2014 the corners won\u2019t move until you unlock.'
    : 'Angle unlocked \u2014 drag the crosshairs to adjust.');
}
lockBtn.addEventListener('click', ()=>setCornersLocked(!S.cornersLocked));
paintLock();

/* #calibImg used to rely on CSS `max-width/max-height:100%` to fit inside
   #calibWrap, with #calibWrap (inline-block) hugging the image so its rect
   could stand in for the image's own displayed box everywhere (loupe math,
   handle drag math, the corner-polygon overlay). That assumption silently
   breaks for tall/portrait photos: a percentage `max-height` only resolves
   against an ancestor with a *specified* (non-auto) height, and #calibWrap's
   own height is auto (content-based) — so `max-height:100%` on the image was
   never actually being applied, letting portrait photos render far taller
   than #calibWrap and overflow past it. Everything measuring `wrap`'s rect
   was then off by exactly that overflow, which is what made the loupe (and
   corner dragging) point at the wrong part of the photo. Setting the image's
   box in px from here sidesteps the percentage-resolution rule entirely, so
   #calibWrap (still auto-sized) hugs the image's *real* rendered box again.  */
export function fitPhoto(){
  if(!S.photo) return;
  const stage = $('#stage');
  // The photo now shares its column with the step spine above and the
  // footer bar below (see #calibCard in index.html), and its row with the
  // settings panel beside it — sizing against the raw stage box the way
  // this used to, before either existed, let the image claim space they
  // needed too and push the footer down under it. Measuring what they
  // actually take (0 while #calibCard is hidden, which is harmless — see
  // ui/chrome.js's goStage, which re-fits on every real reveal) keeps the
  // budget honest instead of guessing fixed pixel amounts that would drift
  // the moment any of their padding/font-size changes.
  const bar = document.querySelector('.calibbar'), foot = document.querySelector('.calibfoot');
  const panel = $('#calibPanel');
  // Width's budget comes from #calibCard, not #stage — #calibCard has its
  // own `width:min(900px,92vw)` cap (index.html/app.css), which on a narrow
  // window makes it meaningfully narrower than #stage itself. Using
  // stage.clientWidth here let this compute a width wider than the flex
  // row (.calibrow, #calibWrap beside the fixed-width #calibPanel) could
  // actually give #calibWrap. #calibImg's own max-width:100% then clipped
  // the rendered width down to fit while its explicit, unrelated height
  // stayed exactly as set — a non-uniform stretch, worst on a tall/narrow
  // window where width (not height) is the binding constraint. Height has
  // no analogous cap on #calibCard, so stage.clientHeight stays correct.
  const card = $('#calibCard');
  const availW = card.clientWidth - panel.offsetWidth - 22 - 24;
  const availH = stage.clientHeight - bar.offsetHeight - foot.offsetHeight - 32 - 24;
  const scale = Math.min(availW/S.photo.w, availH/S.photo.h, 1);
  cimg.style.width  = (S.photo.w*scale)+'px';
  cimg.style.height = (S.photo.h*scale)+'px';
}

/* Everything that happens once there IS a photo, whatever it came from.
   Split out of loadPhoto so the bundled example can reuse it without going
   through the file picker or the crop step. */
function applyPhoto(photo, message){
  S.photo = photo;
  originalPhotoFor = photo.url;
  cimg.src = photo.url; $('#siteImg').src = photo.url;
  $('#drop').classList.add('hidden');
  $('#calibCard').classList.remove('hidden');
  /* applyDoc hides this when a document arrives with no photo (see
     services/projects.js), and nothing put it back — so New project followed by
     choosing a photo left the photo loaded, the card shown, the image correctly
     sized, and the whole thing display:none. There was no way to mark corners
     without reloading the page. Anything that hides it has to have a partner
     that shows it again, and this is the one path every photo arrives by. */
  wrap.classList.remove('hidden');
  fitPhoto();
  buildHandles();
  setCornersLocked(false);
  setRecropVisible();
  renderRails();
  toast(message || 'Photo loaded — drag the four crosshairs onto the screen corners.');
}

/* The photo as it arrived, before any cropping.
   Kept so the crop can be REOPENED and widened again — re-cropping the
   cropped result would be destructive, and there would be no way back out to
   the full frame. Deliberately module state rather than part of S: it would
   otherwise be serialised into every saved project and every cloud upload,
   doubling the photo payload to buy an undo that matters mostly in the first
   minute. The button hides itself when this is empty. */
let originalPhotoUrl = null;
/* Which photo the kept original actually produced. The button is offered only
   when S.photo is still THAT photo — module state outlives a project, so
   without this pairing, starting a new project or opening a saved one (which
   has no original: only the cropped image is saved) would leave the button
   pointing at the previous project's picture, and "Adjust crop" would silently
   swap in a different photograph. Fails closed: any mismatch hides it. */
let originalPhotoFor = null;
/* How the photo currently on screen was cut out of originalPhotoUrl, normalised
   to that original — the shared space a re-crop maps corner marks through (see
   recropPhoto). null means there isn't a rect-only answer, either because no
   photo has been through the cropper yet or because a rotate/straighten baked
   new content in, at which point the two frames no longer relate by a rect. */
let photoCrop = null;

export function setRecropVisible(){
  const b = $('#btnRecrop');
  if(!b) return;
  b.classList.toggle('hidden', !originalPhotoUrl || S.photo?.url !== originalPhotoFor);
}

/* Reopen the crop editor on the original frame. Cancelling leaves everything
   exactly as it was. */
async function recropPhoto(){
  if(!originalPhotoUrl) return;
  const cropped = await cropPhoto(originalPhotoUrl);
  if(!cropped) return;                    // cancelled — current crop untouched

  /* Carry the corner marks across, so they stay on the same pixels of the
     photograph instead of on the same fractions of a frame that just changed
     shape. photoCrop is how the CURRENT photo was cut from the original;
     cropped.rect is how the new one is. Both are normalised to that same
     original, which is what makes them comparable — see core/crop-map.js.

     Either being unavailable (a rotate or straighten in either pass) means
     there is no honest rect-only answer, and the old behaviour stands: leave
     the numbers where they are and ask for a look. */
  const moved = (photoCrop && !cropped.rotated)
    ? remapCorners(S.corners, photoCrop, cropped.rect)
    : null;
  if(moved) S.corners = moved.corners;
  photoCrop = cropped.rotated ? null : cropped.rect;

  /* Cropping changes the frame the lens is described in — the field of view and
     the principal point both move — so the previous solve no longer describes
     this photo even when the corners themselves came across perfectly.
     applyPhoto unlocks the angle; this clears the stale result behind it. */
  S.solved = false;
  applyPhoto(cropped, moved
    ? (moved.clipped
        ? 'Crop updated — a corner fell outside the new crop and is pinned to the edge. Check all four.'
        : 'Crop updated — the corner marks moved with the photo. Lock the angle again to re-solve.')
    : 'Crop updated — check the four crosshairs still sit on the screen corners.');
}

/* Takes anything FileReader accepts — a File from the picker, or a Blob
   fetched from the bundled example. */
/** A Blob, or the promise of one — see loadExamplePhoto, which has to fetch
 *  its Blob and would otherwise leave the click unanswered while it did. */
function readAsDataURL(file){
  return new Promise((ok, no) => {
    const rd = new FileReader();
    rd.onload = () => ok(rd.result);
    rd.onerror = () => no(rd.error || new Error('That file could not be read.'));
    rd.readAsDataURL(file);
  });
}

async function loadPhoto(file, message, { array = false } = {}){
  /* HANDED TO THE DIALOG AS A PROMISE, so it opens now and fills in when the
     bytes arrive. Everything this used to wait for first — the read, and a
     whole extra Image decode whose only purpose was to be waited on, since
     cropPhoto decodes the photo itself — happened between the click and any
     sign that the click had landed.

     The extra decode is simply gone. It loaded the same data URL into an
     Image that nothing then looked at. */
  const url = Promise.resolve(file).then(readAsDataURL);
  const cropped = await cropPhoto(url);
  if(!cropped) return;                    // cancelled, or the photo never arrived
  originalPhotoUrl = await url;           // the untouched original, for a later re-crop
  photoCrop = cropped.rotated ? null : cropped.rect;
  /* The wall is made only once the photo is: backing out of the picker or the
     crop must leave the start screen exactly as it was, not half a wall. */
  if(array && !startArray()) return;
  applyPhoto(cropped, array
    ? 'Set the wall’s size in the panel, then drag the four crosshairs onto the outside corners of the whole wall.'
    : message);
}

/* The bundled example, for someone who has no photograph of a screen to hand
   — which is nearly everyone opening this for the first time at a desk. It
   replaces the one asset they are missing and nothing else: the corners are
   NOT pre-marked, so they do the same calibration they would do with their
   own photo. Marking four corners is the thing this app is for; landing
   someone in a solved scene would skip it.

   Goes through loadPhoto, so it takes exactly the same route as a real
   upload — crop step included. A Blob is all FileReader needs, so nothing
   here has to duplicate the read, the decode or the apply. The crop stage
   also earns its place on this path: the example is someone else's
   photograph, and framing it is the first thing they might reasonably want
   to change about it. */
function loadExamplePhoto(){
  /* NOT AWAITED HERE. The fetch is handed straight through to loadPhoto, which
     hands it to the crop dialog, which opens on the spot and downloads behind
     itself. Awaited at this level the click bought nothing for three seconds on
     a slow connection — 137 kB over the wire, then a read, then a decode, and
     only then a dialog. */
  const blob = fetch('/example/screen.jpg').then(res => {
    if(!res.ok) throw new Error('HTTP ' + res.status);
    return res.blob();
  });
  /* Reported separately from the dialog, which closes itself on a failure but
     cannot say what it was trying to load. */
  blob.catch(err => {
    console.error('[illusio] example photo failed to load', err);
    toast('Could not load the example photo. Choose one of your own instead.', true);
  });
  loadPhoto(blob, 'Example photo loaded — drag the four crosshairs onto the screen corners.');
  /* The only question worth asking about this feature: whether it is what
     people actually start with. */
  track('example_photo');
}

/* Swap the reference photo without losing the composition. Objects, the recess
   and materials all stay; you re-mark the four corners for the new screen (the
   old marks are kept as a starting point) and re-solve. This is how you move a
   finished piece onto a differently-sized or differently-placed screen. */
async function replacePhoto(file){
  /* Same shape as loadPhoto: the dialog opens on the click and the read
     happens behind it, and the extra Image decode that used to gate it is
     gone — cropPhoto decodes the photo itself. */
  const url = Promise.resolve(file).then(readAsDataURL);
  const cropped = await cropPhoto(url);
  if(!cropped) return;                    // cancelled — old photo is untouched
  S.photo = cropped;
  /* The replacement gets the same second chance at its crop the first
     photo had — the crop dialog it just came through is exactly where
     someone realises they cut a corner off. */
  originalPhotoUrl = await url;
  originalPhotoFor = cropped.url;
  photoCrop = cropped.rotated ? null : cropped.rect;
  cimg.src = cropped.url; $('#siteImg').src = cropped.url;
  // keep S.corners as-is so the previous framing is the starting point
  S.solved = false;
  setCornersLocked(false);
  setRecropVisible();
  goStage('calib');
  fitPhoto();
  buildHandles(); paintHandles();
  renderRails();
  toast('Photo replaced — re-mark the corners for the new screen. Your objects are kept.');
}


/* Which handles exist, and what each one moves. Four for a single screen; six
   for a corner — and two of those six are SHARED. 'tr' and 'br' are the first
   panel's right edge, which is by definition the second panel's left edge:
   there is exactly one copy of those two points, so nothing has to keep the
   panels in step and they cannot drift apart. That is the whole reason
   S.cornerPanel does not store the shared edge itself, and it is why there is
   no synchronisation code anywhere below. */
function handleSpecs(){
  const corner = !!S.cornerPanel;
  return [
    { k:'tl',  label: CLABEL.tl },
    { k:'tr',  label: corner ? 'EDGE' : CLABEL.tr, edge: corner, right: true },
    { k:'br',  label: corner ? 'EDGE' : CLABEL.br, edge: corner, right: true },
    { k:'bl',  label: CLABEL.bl },
    ...(corner ? [{ k:'ctr', label:'TR', right:true }, { k:'cbr', label:'BR', right:true }] : [])
  ];
}
/** The point a handle moves — the live object, so callers mutate it in place. */
function handlePt(k){
  if(k === 'ctr') return S.cornerPanel.outer.tr;
  if(k === 'cbr') return S.cornerPanel.outer.br;
  return S.corners[k];
}

export function buildHandles(){
  wrap.querySelectorAll('.handle').forEach(n=>n.remove());
  handleSpecs().forEach(({ k, label, edge, right })=>{
    const h = el('div','handle'+(right?' r':'')+(edge?' handle--edge':''));
    h.dataset.k = k;
    h.tabIndex = 0;
    h.setAttribute('role','slider');
    h.setAttribute('aria-label', edge ? 'Shared corner edge' : label+' corner');
    h.appendChild(el('i'));
    h.appendChild(el('b',null,label));
    h.addEventListener('pointerdown', onHandleDown);
    h.addEventListener('keydown', ev=>{
      // Same Shift-coarse/Alt-fine convention as every drag-to-scrub handle
      // in controls.js (makeScrub) — Shift moves 10x further per press,
      // Alt 10x less, for sub-pixel corner nudges on a large photo.
      const mult = ev.shiftKey ? 10 : (ev.altKey ? .1 : 1);
      const step = 0.001 * mult;
      const d = {ArrowLeft:[-step,0],ArrowRight:[step,0],ArrowUp:[0,-step],ArrowDown:[0,step]}[ev.key];
      if(!d) return;
      if(S.cornersLocked) return;           // frozen while the angle is locked
      ev.preventDefault();
      const c = handlePt(k);
      c.x = clamp(c.x+d[0],0,1);
      c.y = clamp(c.y+d[1],0,1);
      paintHandles();
    });
    wrap.appendChild(h);
  });
  // Laying out fresh handles is not the user moving them — see paintHandles.
  paintHandles(false);
}

/**
 * @param {boolean} [dirty] Whether this repaint should be treated as the
 *   corners having moved. Dragging a handle or nudging one with arrow keys
 *   IS that; a resize repositioning the same corner values, or an initial
 *   layout right after loading a document — buildHandles(), applyDoc() —
 *   is not, and must not silently un-solve a calibration that never
 *   actually changed. Defaults to true because most callers ARE a real move;
 *   the handful that aren't pass false explicitly.
 */
export function paintHandles(dirty=true){
  wrap.querySelectorAll('.handle').forEach(h=>{
    const c = handlePt(h.dataset.k);
    h.style.left = (c.x*100)+'%';
    h.style.top  = (c.y*100)+'%';
  });
  const r = wrap.getBoundingClientRect();
  const at = k => { const c = handlePt(k); return `${c.x*r.width},${c.y*r.height}`; };
  const pts = ks => ks.map(at).join(' ');

  /* Fill and outline are separate elements on purpose. When both panels were
     stroked polygons the shared edge got drawn TWICE, once by each — and then
     the hinge on top of that, three strokes deep, which read as a doubled line
     down the middle. Each panel now fills as a polygon with no stroke and
     outlines as an OPEN polyline that skips the shared edge, so the hinge is
     drawn exactly once, by itself, in its own colour. */
  poly.setAttribute('points', pts(CK));
  const polyR = $('#calibPolyR'), hinge = $('#calibHinge');
  const edgeL = $('#calibEdgeL'), edgeR = $('#calibEdgeR');
  const corner = !!S.cornerPanel;
  if(polyR) polyR.classList.toggle('hidden', !corner);
  if(hinge) hinge.classList.toggle('hidden', !corner);
  if(edgeR) edgeR.classList.toggle('hidden', !corner);

  if(corner){
    polyR.setAttribute('points', pts(['tr','ctr','cbr','br']));
    // each outline runs the three edges that are NOT the corner
    edgeL.setAttribute('points', pts(['tr','tl','bl','br']));
    edgeR.setAttribute('points', pts(['tr','ctr','cbr','br']));
    const a = handlePt('tr'), b = handlePt('br');
    hinge.setAttribute('x1', a.x*r.width); hinge.setAttribute('y1', a.y*r.height);
    hinge.setAttribute('x2', b.x*r.width); hinge.setAttribute('y2', b.y*r.height);
  }else{
    // one screen: the outline closes back on itself
    edgeL.setAttribute('points', pts(['tl','tr','br','bl','tl']));
  }
  if(dirty && S.solved) markStale();
  requestRender();
}

function onHandleDown(ev){
  if(S.cornersLocked) return;               // frozen while the angle is locked
  const h = ev.currentTarget, k = h.dataset.k;
  h.setPointerCapture(ev.pointerId);
  h.classList.add('drag');
  // Was a direct absolute mouse-position → corner-position mapping, which
  // has no room for a speed modifier. Incremental instead: each move event
  // applies just its own since-last-event delta, scaled by the same
  // Shift-coarse(10x)/Alt-fine(0.1x) convention as the keyboard nudge above
  // and every scrub handle in controls.js. At the default 1x this sums to
  // exactly the same total motion as the old absolute mapping, so a drag
  // with no modifier held feels identical — the only change is that toggling
  // Shift/Alt mid-drag now smoothly changes the rate from that point on,
  // rather than needing a fixed start point that a rescaled multiplier would
  // otherwise retroactively jump.
  let lastX = ev.clientX, lastY = ev.clientY;
  const move = e=>{
    const r = wrap.getBoundingClientRect();
    const mult = e.shiftKey ? 10 : (e.altKey ? .1 : 1);
    const c = handlePt(k);
    c.x = clamp(c.x + (e.clientX-lastX)/r.width*mult, 0, 1);
    c.y = clamp(c.y + (e.clientY-lastY)/r.height*mult, 0, 1);
    lastX = e.clientX; lastY = e.clientY;
    paintHandles();
    showLoupe(r, c.x*r.width, c.y*r.height);
  };
  const up = e=>{
    h.classList.remove('drag');
    loupe.style.display='none';
    h.removeEventListener('pointermove',move);
    h.removeEventListener('pointerup',up);
    h.removeEventListener('pointercancel',up);
  };
  h.addEventListener('pointermove',move);
  h.addEventListener('pointerup',up);
  h.addEventListener('pointercancel',up);
  setLoupeImage();
  const r0 = wrap.getBoundingClientRect();
  showLoupe(r0, handlePt(k).x*r0.width, handlePt(k).y*r0.height);
}

/* Re-decoding a multi-megabyte data: URL is ~50-60x more expensive than just
   moving the crop window (measured: ~8ms per call for an 8000x6000 photo, and
   real camera JPEGs run bigger than that) — so the image is only assigned once
   per drag, in onHandleDown. Re-assigning the SAME url() on every pointermove
   was costing enough main-thread time, scaling with the photo's file size, that
   the loupe visibly lagged behind the cursor on large photos: it looked like a
   position bug but was a paint-cost bug. */
let loupeImgUrl = null;
function setLoupeImage(){
  if(loupeImgUrl === S.photo.url) return;
  loupeImgUrl = S.photo.url;
  loupe.style.backgroundImage = `url(${loupeImgUrl})`;
}

/**
 * Anchored to the CORNER, not the cursor — both the widget's on-screen
 * position and the patch of photo it zooms into. The two used to be the
 * same point by construction (dragging was a direct 1:1 mouse→corner
 * mapping), but Shift/Alt (see onHandleDown) can now put the corner
 * somewhere the cursor isn't. Anchoring only the zoomed CONTENT to the
 * corner while leaving the widget itself following the cursor looked worse,
 * not better — a magnifier sitting under your mouse showing a patch of
 * photo from wherever the corner happened to be, nowhere near what it was
 * floating over. Following the corner for both keeps it showing exactly
 * what it's sitting on, same as before this ever diverged.
 * @param {DOMRect} r - the photo wrap's rect, for scaling the zoomed image.
 * @param {number} px @param {number} py - the corner's position, in
 *   wrap-local pixels.
 */
function showLoupe(r, px, py){
  const Z = 5, R = 66;
  loupe.style.display = 'block';
  loupe.style.backgroundSize = `${r.width*Z}px ${r.height*Z}px`;
  loupe.style.backgroundPosition = `${-(px*Z-R)}px ${-(py*Z-R)}px`;
  const cx = r.left + px, cy = r.top + py;
  let lx = cx - 150, ly = cy - 150;
  if(lx < 8) lx = cx + 22;
  if(ly < 8) ly = cy + 22;
  loupe.style.left = lx+'px';
  loupe.style.top  = ly+'px';
}

export function markStale(){
  /* A PLACEMENT-ONLY PROJECT CANNOT GO STALE, so this is where that is decided
     — not at the five call sites, which is where it was tried first and where
     it was immediately found to be missing from a sixth.

     An array's four marks place a mockup and nothing else: no solve ran, and
     the footer deliberately offers no Lock angle, so "the corners moved, lock
     the angle again" names an action that does not exist there. Left in, the
     ordinary act of dragging a corner disabled Export for good, saved the flag
     into the file, and left the project with no way back.

     Staleness is a fact about the PROJECT, not about who noticed the change,
     so it belongs to the one function that asserts it. */
  if(placementOnly()) return;

  S.solved = false;
  setChip('warn','Corners moved — lock the angle again');
  $('#btnExport').disabled = true;
}
export function setChip(kind, text){
  const c = $('#solveChip');
  c.className = 'chip' + (kind?' '+kind:'');
  c.querySelector('span').textContent = text;
  // Nothing to say in the top row when things are fine (calibrated) or
  // just not done yet (the default, pre-calibration state — the disabled
  // Design/Export tabs already say that) — only a real problem (corners
  // moved, solve failed) is worth a permanent, always-visible chip.
  c.classList.toggle('hidden', kind !== 'warn');
}

/* Whether the marked quad reads as the opposite orientation (portrait vs
   landscape) from the currently-set pixel resolution — e.g. corners marked
   on a tall screen while Pixels across/down is still set to a 16:9
   landscape default. Perspective distorts a quad's exact proportions, but
   it essentially never flips which pair of opposite edges is the longer
   one for a realistic photo angle, so this is a safe signal even though
   it can't recover the true aspect ratio the way the corners + a known
   screen size can. A near-square photo quad (ratio < 1.08) is left alone —
   too close to call, and a square screen has no orientation to mismatch. */
function orientationMismatch(){
  if(!S.photo || S.res.w === S.res.h) return false;
  const pt = k => ({ x: S.corners[k].x*S.photo.w, y: S.corners[k].y*S.photo.h });
  const tl=pt('tl'), tr=pt('tr'), br=pt('br'), bl=pt('bl');
  const dist = (a,b)=>Math.hypot(a.x-b.x, a.y-b.y);
  const horiz = (dist(tl,tr)+dist(bl,br))/2;
  const vert  = (dist(tl,bl)+dist(tr,br))/2;
  const lo = Math.min(horiz,vert), hi = Math.max(horiz,vert);
  if(lo < 1e-6 || hi/lo < 1.08) return false;
  return (horiz >= vert) !== (S.res.w >= S.res.h);
}

export function doSolve(){
  /* The auto-flip reads ONE quad and swaps its width for its height, and in
     corner mode that quad IS the left panel — the thing being swapped. It
     stayed switched off here at first on the theory that the second panel
     would have to be carried along; it does not. The second panel's width and
     pixels across are independent by design, and its height is not its own at
     all — it is the shared edge, so it follows the flip for free. */
  /* Never for a wall: its shape is the grid you laid out, and swapping the
     screen's sides would turn the whole layout without touching the grid. */
  if(!S.screenArray && orientationMismatch()){
    [S.res.w, S.res.h] = [S.res.h, S.res.w];
    [S.screen.w, S.screen.h] = [S.screen.h, S.screen.w];
    onResChange(); onScreenChange(); renderLeft();
    toast('The marked corners look like the other orientation — flipped Pixels across/down to match.');
  }
  const r = solveCalibration({ photo: S.photo, corners: S.corners, screen: S.screen, focal: S.focal });
  if(r.error){ setChip('warn','Angle not locked'); toast(r.error, true); return; }
  S.eye = new THREE.Vector3(r.eye[0], r.eye[1], r.eye[2]);
  S.focal.px = r.f; S.focal.method = r.method;
  S.solved = true;
  /* A camera object outranks S.eye (eyeVec), so a solve that left it where it
     was would report a viewer the scene then ignored. */
  const cam = S.objects.find(o => o.type === 'camera');
  if(cam){ cam.pos = { x: r.eye[0], y: r.eye[1], z: r.eye[2] }; syncTransform(cam); }
  setChip('ok','Angle locked');
  $('#btnExport').disabled = false;
  $('.step[data-stage=compose]').disabled = false;
  $('.step[data-stage=animate]').disabled = false;
  const corner = S.cornerPanel ? solveCornerPanel({
    photo: S.photo, pose: r.pose, screen: S.screen,
    cornerW: S.cornerPanel.w, outer: S.cornerPanel.outer
  }) : null;
  if(corner && !corner.error) S.cornerPanel.convex = corner.convex;
  /* Kept from the UNREFINED solve, deliberately. cornerCheck reports this, and
     it only means anything while the two outer marks are strangers to the fit
     — refine first and the residual reports how well the optimiser did rather
     than whether the corner is square. */
  cornerResult = corner;

  /* Now, and only now, let those marks push back on the camera.
     The solve above places the viewer from panel one's four marks alone, so
     every bit of error the marks carry lands on panel two: panel one's
     homography is fitted to its own quad and hides anything the pose got
     wrong. Flat output stays right either way — each panel renders through its
     own frustum with no photograph involved — and the whole of it surfaces
     On-site, at the seam, growing the further content stands off the screen.
     Sharing the error across all six marks is what closes that.

     Nothing about the geometry moves: the corner is still 90°, the panels are
     still the sizes entered. Only the camera does. */
  if(corner && !corner.error){
    const ref = refineCornerPose({
      photo: S.photo, corners: S.corners, screen: S.screen,
      cornerW: S.cornerPanel.w, outer: S.cornerPanel.outer,
      pose: r.pose, convex: corner.convex
    });
    if(!ref.error){
      S.eye = new THREE.Vector3(ref.eye[0], ref.eye[1], ref.eye[2]);
      S.focal.px = ref.f;
      S.focal.method = r.method + ', refined on both panels';
      cornerRefine = ref;
    }else{
      cornerRefine = null;
    }
  }else{
    cornerRefine = null;
  }

  rebuildHelpers(); frameOrbit(); seedRecess(); renderRails(); requestRender(); resetHistory();
  toast(`Locked. Viewer sits ${fmt(S.eye.z,2)} m out, ${fmt(S.eye.x,2)} m across, ${fmt(S.eye.y,2)} m up from screen centre.`
        + cornerSentence(corner));
}

/* What the second panel's two marks say, once the camera is known from the
   first panel's four. They took no part in that solve, so this is a genuine
   independent check of the two things a corner screen assumes: that the corner
   is square, and that the panels are the same height. Said in metres at the
   outer edge as well as pixels — a degree of error is centimetres out there,
   and centimetres are the only unit anyone can judge before cutting. */
export let cornerResult = null;
/* What the six-mark refinement did, for the readout. Null when there is no
   corner, or when the refinement declined to improve on the original. */
export let cornerRefine = null;
/* The same check, run on demand so the Calibrate panel can show it live rather
   than only in the toast that flashes past at solve time. Cheap — two
   back-projections — so it can be called on every rail rebuild. */
export function cornerCheck(){
  if(!S.cornerPanel || !S.photo || !S.solved) return null;
  const cal = solveCalibration({ photo:S.photo, corners:S.corners, screen:S.screen, focal:S.focal });
  if(cal.error || !cal.pose) return null;
  const r = solveCornerPanel({ photo:S.photo, pose:cal.pose, screen:S.screen,
                               cornerW:S.cornerPanel.w, outer:S.cornerPanel.outer });
  if(r.error) return r;
  /* The reprojection error said in METRES at the outer edge as well as pixels.
     A degree of corner error is centimetres out there, and centimetres are the
     only unit anyone can judge before something gets cut. */
  const perPx = S.cornerPanel.w / Math.max(1, Math.hypot(
    (S.cornerPanel.outer.tr.x - S.corners.tr.x) * S.photo.w,
    (S.cornerPanel.outer.tr.y - S.corners.tr.y) * S.photo.h));
  return { ...r, reprojM: (r.reprojPx || 0) * perPx };
}
function cornerSentence(c){
  if(!c) return '';
  if(c.error) return ' Second panel: ' + c.error;
  const off = c.widthErrM;
  const bits = [` Second panel measures ${fmt(c.wMeasured,2)} m`];
  if(off > .02 && c.widthTrustworthy) bits.push(`against the ${fmt(S.cornerPanel.w,2)} m entered`);
  if(c.reprojPx > 3) bits.push(`— marks are ${Math.round(c.reprojPx)} px out, so the real corner may not be square`);
  return bits.join(' ') + '.';
}

/* The recess is the starting point for almost every anamorphic build, so put one
   in the scene once, fitted to the screen. Delete it and it stays deleted. */
/* Seeded once, on the first solve. The default preset is Dark, so a lamp ships
   alongside the recess — otherwise the first thing anyone sees is a black frame. */
function seedRecess(){
  if(S.seeded || S.objects.length) return;
  S.seeded = true;
  const rec = addObject('recess');
  select(rec.id);        // the recess is what you'll shape first
}

export function manualSetup(){
  const d = Math.hypot(S.screen.w,S.screen.h)*1.5;
  S.eye = new THREE.Vector3(0,0,d);
  S.focal.px = null; S.focal.method = 'manual viewer';
  S.solved = true;
  setChip('ok','Viewer set by hand');
  $('#btnExport').disabled = false;
  $('.step[data-stage=compose]').disabled = false;
  $('.step[data-stage=animate]').disabled = false;
  seedRecess();
  // Only one camera object ever makes sense — it IS the viewer, and
  // eyeVec()/cameraObj() (scene/helpers.js) only ever look at the first
  // one in S.objects, so a second would just sit there doing nothing
  // while looking like it should do something. Re-running manual setup
  // (e.g. from Calibrate's footer, after already using it once) reuses
  // whichever camera is already there instead of adding another.
  let cam = S.objects.find(o=>o.type==='camera');
  if(cam){ cam.pos = {x:0, y:0, z:d}; syncTransform(cam); }
  // Setting up by hand means placing the viewpoint yourself, so give it a real
  // object to drag rather than three numeric fields.
  else cam = addObject('camera');
  rebuildHelpers(); frameOrbit();
  select(cam.id);
  goStage('compose'); resetHistory();
  toast('Camera placed square to the screen. Drag it to set the viewpoint.');
}

/* A VIDEO WALL, set up by numbers.

   The same door manualSetup opens, with the wall described first. There is no
   photo on this path and no corners to mark — a grid of displays is specified,
   not measured off a shot — so it ends in exactly the same place: a camera you
   drag, or a focal length you type at it (ui/rail-right.js's lens field).

   The default is the commonest thing anyone builds: two 1080p landscape screens
   side by side with a 2 cm gap. Everything about it is editable afterwards in
   Scene Settings, so this only has to be a plausible starting shape rather than
   a guess at the user's actual wall.

   onScreenChange does the rest — syncArrayWall writes the wall these numbers
   add up to into S.screen/S.res, and the inset refit that follows fits one
   opening across the whole thing, which is the point of the feature. */
/* The wall itself, without a viewer — shared by both ways in. Returns false when
   the licence gate refuses, having said why. */
function startArray(){
  /* Gated at the ACTION, not only on the button, for the reason goStage gives
     about the Motion stage: a gate that lives on one control is a gate with as
     many ways around it as there are ways to reach the thing. Explains and
     offers the way in rather than failing silently — a button that does nothing
     reads as broken, which is a worse impression than one that says no. */
  const gate = canUseScreenArray();
  if(!gate.ok){
    toast(gate.reason, true, { text: MOTION_CTA, onClick: showTrial });
    return false;
  }
  S.cornerPanel = null;                 // exclusive; see store.js's note
  S.screenArray = { cols: 2, rows: 1, gap: .02,
                    tile: { w: 1.21, h: .68 }, res: { w: 1920, h: 1080 } };
  /* The seams live in the screen-frame helper, which is off by default. On a
     single screen that is the right default — the frame is the edge of the
     render and you can see it anyway. On a wall it is the only thing showing
     where one display stops and the next starts, so setting up an array is
     exactly the moment to turn it on. Still a preference: Settings can turn
     it straight back off and nothing here fights that. */
  S.showHelpers = true;
  onScreenChange();
  return true;
}

/* A VIDEO WALL WITH NO PHOTO: the viewer starts square-on and is placed by hand. */
export function arraySetup(){
  if(!startArray()) return;
  manualSetup();
  /* manualSetup selects the Camera, which is right for its own path — there the
     camera IS the thing you have just been given to move. Here the wall is, and
     it is what you have questions about, so the array takes the selection back.
     After manualSetup, not before: select() clears arraySelected. */
  select(null);
  S.arraySelected = true;
  renderRails();
  toast('Two screens side by side. Set the grid, size and margin under LCD Video Wall.');
}

/* drag & drop */
const drop = $('#drop');
['dragenter','dragover'].forEach(e=>drop.addEventListener(e,ev=>{ev.preventDefault();drop.classList.add('over');}));
['dragleave','drop'].forEach(e=>drop.addEventListener(e,ev=>{ev.preventDefault();drop.classList.remove('over');}));
drop.addEventListener('drop', ev=>{ const f=ev.dataTransfer.files[0]; if(!f) return; if(/\.glb$/i.test(f.name)) importGLB(f); else if(f.type.startsWith('image/')) loadPhoto(f); });
/* Which door the next chosen photo came through. A picker that is cancelled
   fires nothing, so every opener sets this rather than only the wall's. */
let photoForWall = false;
$('#btnPhoto').onclick = ()=>{ photoForWall = false; $('#filePhoto').click(); };
$('#btnArrayPhoto').onclick = ()=>{
  /* Refused before the picker opens, not after a photo has been chosen and
     cropped — arraySetup gates at the action for the same reason. */
  const gate = canUseScreenArray();
  if(!gate.ok){ toast(gate.reason, true, { text: MOTION_CTA, onClick: showTrial }); return; }
  photoForWall = true; $('#filePhoto').click();
};
$('#btnExample').onclick = loadExamplePhoto;
$('#btnRecrop').onclick = recropPhoto;
$('#btnReplace').onclick = ()=>$('#fileReplace').click();
$('#fileReplace').onchange = e=>{ if(e.target.files[0]) replacePhoto(e.target.files[0]); e.target.value=''; };
/* A FILMED VIDEO AS THE REFERENCE. The corners are marked on one frame of it, which the person
   chooses; that frame goes through the same loadPhoto as any photograph (crop step included), and
   the video itself is kept for the on-site export, which renders over it frame by frame. See
   export/site-clip.js for what has to hold for that to be honest — a fixed camera, and no rotation. */
const isVideoFile = f => String(f.type).startsWith('video/') || /\.(mp4|mov|m4v|webm)$/i.test(f.name || '');
async function loadVideoReference(file, wall){
  let clip;
  try { clip = await openClip(file); }
  catch(err){ toast(err.message || 'That video could not be opened.', true); return; }
  const frame = await pickFrame(clip);
  if(!frame){ clip.close(); return; }                    // cancelled
  const blob = await new Promise(ok => frame.toBlob(ok, 'image/jpeg', 0.93));
  const before = S.photo;
  await loadPhoto(blob, 'Frame taken from the video — drag the four crosshairs onto the screen corners. '
    + 'The on-site render can then be made over the whole video.', { array: wall });
  if(!S.photo || S.photo === before){ clip.close(); return; }   // the crop was cancelled
  try { adoptSiteClip(clip); track('site_video_reference'); }
  catch(err){ clip.close(); toast(err.message + ' The video will not be used behind the on-site render.', true); }
}
$('#filePhoto').onchange = e=>{
  const f = e.target.files[0], wall = photoForWall;
  photoForWall = false;
  if(f){
    if(isVideoFile(f)) loadVideoReference(f, wall);
    else loadPhoto(f, undefined, { array: wall });
  }
  e.target.value='';
};
$('#btnManual').onclick = manualSetup;
$('#btnArray').onclick = arraySetup;
// Same escape hatch as #btnManual above (before a photo is chosen), just
// reachable once you're already marking corners too — manualSetup doesn't
// care whether S.photo is set, it just places a draggable camera and moves
// on, so there's nothing to unwind first.
/* #calibManual2 is gone from the footer — the same escape hatch is now a
   quiet control in the Lens panel, wired there (ui/rail-left.js). */

/* PHOTO OR PLAN. One column, two views: the photograph you mark, and the scale
   drawing of what you are marking. Photo is the default because marking is what
   this stage is for — the drawing is here to be consulted, not worked in. */
export function showCalibView(which){
  const plan = which === 'plan';
  $('#calibWrap').classList.toggle('hidden', plan);
  $('#planWrap').classList.toggle('hidden', !plan);
  // the graph paper is the drawing's surface, not the photograph's
  $('.calibstage').classList.toggle('calibstage--plan', plan);
  for(const [id, on] of [['#tabPhoto', !plan], ['#tabPlan', plan]]){
    $(id).classList.toggle('on', on);
    $(id).setAttribute('aria-selected', on ? 'true' : 'false');
  }
  syncPlan();          // fits the photo to the stage, and redraws the plan
}
/* The stage's height comes from the window, so the photo's ceiling has to be
   re-measured when the window changes — otherwise it keeps yesterday's number
   and either overflows the box or sits small in it. */
addEventListener('resize', () => { fitCalibStage(); paintHandles(false); });
$('#tabPhoto').onclick = ()=>showCalibView('photo');
$('#tabPlan').onclick  = ()=>showCalibView('plan');
// Doesn't touch S.photo/S.corners at all — just steps the view back to the
// picker. Choosing a new photo from there overwrites everything through the
// normal loadPhoto() path; closing the tab with nothing chosen leaves
// nothing to clean up either.
$('#calibBack').onclick = ()=>{
  $('#calibCard').classList.add('hidden');
  $('#drop').classList.remove('hidden');
};
